import { DOCX_ERRORS, DOCX_LIMITS } from './constants';
import { DocxPreviewError } from './DocxPreviewError';

const ZIP_LOCAL_HEADER = 0x04034b50;
const ZIP_CENTRAL_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIR = 0x06054b50;
const EOCD_MIN_SIZE = 22;
const CENTRAL_HEADER_MIN_SIZE = 46;
const ZIP64_MARKER_16 = 0xffff;
const ZIP64_MARKER_32 = 0xffffffff;

const fail = (message: string): never => {
  throw new DocxPreviewError(message);
};

const findEndOfCentralDir = (tail: DataView): number => {
  for (let i = tail.byteLength - EOCD_MIN_SIZE; i >= 0; i--) {
    if (tail.getUint32(i, true) === ZIP_END_OF_CENTRAL_DIR) return i;
  }
  return -1;
};

const checkCentralDirectory = async (blob: Blob) => {
  const tailLength = Math.min(blob.size, EOCD_MIN_SIZE + 0xffff);
  const tail = new DataView(
    await blob.slice(blob.size - tailLength).arrayBuffer()
  );

  const eocd = findEndOfCentralDir(tail);
  if (eocd < 0) fail(DOCX_ERRORS.corrupted);

  const totalEntries = tail.getUint16(eocd + 10, true);
  const cdSize = tail.getUint32(eocd + 12, true);
  const cdOffset = tail.getUint32(eocd + 16, true);

  if (
    totalEntries === ZIP64_MARKER_16 ||
    cdSize === ZIP64_MARKER_32 ||
    cdOffset === ZIP64_MARKER_32
  ) {
    fail(DOCX_ERRORS.zip64);
  }
  if (totalEntries > DOCX_LIMITS.maxEntries) fail(DOCX_ERRORS.tooManyParts);
  if (cdOffset + cdSize > blob.size) fail(DOCX_ERRORS.corrupted);

  const directory = new DataView(
    await blob.slice(cdOffset, cdOffset + cdSize).arrayBuffer()
  );

  let offset = 0;
  let totalUncompressed = 0;
  for (let i = 0; i < totalEntries; i++) {
    if (
      offset + CENTRAL_HEADER_MIN_SIZE > directory.byteLength ||
      directory.getUint32(offset, true) !== ZIP_CENTRAL_HEADER
    ) {
      fail(DOCX_ERRORS.corrupted);
    }

    const compressed = directory.getUint32(offset + 20, true);
    const uncompressed = directory.getUint32(offset + 24, true);
    if (compressed === ZIP64_MARKER_32 || uncompressed === ZIP64_MARKER_32) {
      fail(DOCX_ERRORS.zip64);
    }

    totalUncompressed += uncompressed;
    if (totalUncompressed > DOCX_LIMITS.maxUncompressedBytes) {
      fail(DOCX_ERRORS.tooLargeUnpacked);
    }
    if (
      compressed > 0 &&
      uncompressed / compressed > DOCX_LIMITS.maxCompressionRatio
    ) {
      fail(DOCX_ERRORS.suspiciousCompression);
    }
    if (compressed === 0 && uncompressed > 0) fail(DOCX_ERRORS.corrupted);

    const nameLength = directory.getUint16(offset + 28, true);
    const extraLength = directory.getUint16(offset + 30, true);
    const commentLength = directory.getUint16(offset + 32, true);
    offset +=
      CENTRAL_HEADER_MIN_SIZE + nameLength + extraLength + commentLength;
  }
};

/**
 * Rejects non-ZIP, oversized and zip-bomb-like files before they are unpacked.
 * Sizes come from the central directory, so this is a cheap first defence,
 * not a guarantee.
 */
export const validateDocx = async (blob: Blob): Promise<void> => {
  if (blob.size === 0) fail(DOCX_ERRORS.empty);
  if (blob.size > DOCX_LIMITS.maxFileBytes) fail(DOCX_ERRORS.tooLarge);

  const magic = new DataView(await blob.slice(0, 4).arrayBuffer());
  if (magic.byteLength < 4 || magic.getUint32(0, true) !== ZIP_LOCAL_HEADER) {
    fail(DOCX_ERRORS.invalid);
  }

  await checkCentralDirectory(blob);
};
